import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getEventBySlug } from '@/services/api';
import { Calendar, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ImageModal from '@/components/ImageModal';

interface Event {
    id: string;
    title: string;
    description: string;
    start_date: string;
    end_date: string;
    location: string;
    category?: string;
    photo: string;
    slug: string;
    ministry?: {
        id: number;
        name: string;
    };
    galleries?: {
        id: number;
        source: string;
        caption?: string;
        title: string;
    }[];
}

const EventDetails = () => {
    const { slug } = useParams();
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await getEventBySlug(slug as string);
                setEvent(res.data);
            } catch (err) {
                console.error('Error fetching event:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [slug]);

    const handleImageClick = (url: string) => setSelectedImage(url);
    const closeModal = () => setSelectedImage(null);

    const formatDateRange = (start: string, end: string) => {
        const startDate = parseISO(start);
        const endDate = parseISO(end);
        return startDate.toDateString() === endDate.toDateString()
            ? format(startDate, 'PPP')
            : `${format(startDate, 'PPP')} – ${format(endDate, 'PPP')}`;
    };

    const imageSrc = event?.photo?.startsWith('http')
        ? event.photo
        : `https://api.ibadansouthanglicandiocese.org.ng/${event?.photo}`;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin h-10 w-10 border-4 border-primary rounded-full border-b-transparent mb-4"></div>
                    <p className="text-muted-foreground">Loading event details...</p>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-destructive font-semibold text-xl">Event not found.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-16 px-4 md:px-12">
            <div className="max-w-5xl mx-auto space-y-12">
                {/* Event Card */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <Card className="shadow-xl rounded-2xl overflow-hidden border border-border">
                        {event.photo && (
                            <img
                                src={imageSrc}
                                alt={event.title}
                                className="w-full h-80 object-cover"
                            />
                        )}
                        <CardHeader className="bg-muted px-6 py-5">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-2xl sm:text-3xl font-bold text-foreground">
                                    {event.title}
                                </CardTitle>
                                {event.ministry?.name && (
                                    <Badge variant="outline" className="text-sm px-3 py-1 bg-green-100 text-green-800 border-green-300">
                                        {event.ministry.name}
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="px-6 py-6 space-y-5">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 text-muted-foreground text-sm">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    <span>{formatDateRange(event.start_date, event.end_date)}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                    <MapPin className="w-4 h-4" />
                                    <span>{event.location}</span>
                                </div>
                            </div>
                            <p className="text-gray-700 whitespace-pre-line leading-relaxed text-[15px]">
                                {event.description}
                            </p>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Gallery Section */}
                {event.galleries && event.galleries.length > 0 && (
                    <div>
                        <h2 className="text-2xl font-semibold text-foreground mb-5">Event Gallery</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                            {event.galleries.map((img) => (
                                <motion.div
                                    key={img.id}
                                    whileHover={{ scale: 1.02 }}
                                    className="overflow-hidden rounded-xl shadow-md cursor-pointer group"
                                    onClick={() => handleImageClick(img.source)}
                                >
                                    <img
                                        src={img.source}
                                        alt={img.caption || img.title}
                                        className="object-cover w-full h-48 group-hover:brightness-90 transition-all"
                                    />
                                    {img.caption && (
                                        <div className="p-2 text-sm text-muted-foreground bg-muted">
                                            {img.caption}
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Image Modal */}
                {selectedImage && <ImageModal imageUrl={selectedImage} onClose={closeModal} />}
            </div>
        </div>
    );
};

export default EventDetails;
